import { getWidgetReceiptCss } from './print-receipt-styles';
import type { ReceiptItemGroup, ReceiptTicketLine, WidgetReceiptData } from './print-receipt.types';

/**
 * Minimal HTML escaper for interpolating user/tenant strings into the receipt template.
 * The receipt HTML is written into a separate iframe document, but we still escape to
 * avoid breaking the template with names/labels that contain quotes or angle brackets.
 */
function esc(value: string | null | undefined): string {
    if (value === null || value === undefined) return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function renderTicket(ticket: ReceiptTicketLine): string {
    const original = ticket.originalPrice
        ? `<td class="ticket-original">${esc(ticket.originalPrice)}</td>`
        : `<td class="ticket-original"></td>`;
    return `
        <tr>
            <td class="ticket-label">${esc(ticket.label)}</td>
            ${original}
            <td class="ticket-price">${esc(ticket.price)}</td>
        </tr>
    `;
}

function renderGroup(group: ReceiptItemGroup): string {
    const items = group.items
        .map((item) => {
            const name = item.name ? `<div class="receipt-item-name">${esc(item.name)}</div>` : '';
            const date = item.dateInfo
                ? `<div class="receipt-item-date">${esc(item.dateInfo)}</div>`
                : '';
            const tickets = item.tickets.map(renderTicket).join('');
            return `
                <div class="receipt-item">
                    ${name}
                    ${date}
                    <table class="receipt-tickets">${tickets}</table>
                </div>
            `;
        })
        .join('');

    return `
        <div class="receipt-group">
            <div class="receipt-group-name">${esc(group.groupName)}</div>
            ${items}
        </div>
    `;
}

function renderTotalsRow(label: string, amount: string, className?: string): string {
    const cls = className ? ` class="${className}"` : '';
    return `
        <tr${cls}>
            <td class="totals-label">${esc(label)}</td>
            <td class="totals-amount">${esc(amount)}</td>
        </tr>
    `;
}

export function buildReceiptHtml(data: WidgetReceiptData): string {
    const brand = data.logoUrl
        ? `<div class="receipt-brand"><img src="${esc(data.logoUrl)}" alt=""></div>`
        : '';

    const refCell = data.bookingRef
        ? `<div class="receipt-ref">${esc(data.bookingRefLabel)} ` +
          `<span class="receipt-ref-value">${esc(data.bookingRef)}</span></div>`
        : '<div></div>';

    const groups = data.itemGroups
        .filter((g) => g.items.length > 0)
        .map(renderGroup)
        .join('');

    const vatRows = [
        renderTotalsRow(data.exVatLabel, data.exVatAmount),
        ...data.vatLines.map((v) => renderTotalsRow(v.label, v.amount)),
    ].join('');

    const deductionRows = [
        data.promoLabel && data.promoAmount
            ? renderTotalsRow(data.promoLabel, data.promoAmount)
            : '',
        data.giftCardLabel && data.giftCardAmount
            ? renderTotalsRow(data.giftCardLabel, data.giftCardAmount)
            : '',
    ].join('');

    // Member-card coverage reduces the VAT base, so it's rendered ABOVE the VAT breakdown
    // (invoice-style), unlike gift cards (a payment instrument shown below). Empty for guest/legacy.
    const memberCardRow =
        data.memberCardLabel && data.memberCardAmount
            ? renderTotalsRow(data.memberCardLabel, data.memberCardAmount)
            : '';

    const totalRow = renderTotalsRow(data.totalLabel, data.totalAmount, 'grand-total');

    const footer = data.footerNote
        ? `<div class="receipt-footer">${esc(data.footerNote)}</div>`
        : '';

    return `
        ${brand}
        <hr class="receipt-divider">
        <div class="receipt-header">
            <div class="receipt-heading">${esc(data.heading)}</div>
            ${refCell}
        </div>
        <hr class="receipt-divider">
        ${groups}
        <hr class="receipt-divider-dashed">
        <table class="receipt-totals">
            ${memberCardRow}
            ${vatRows}
            ${deductionRows}
        </table>
        <hr class="receipt-divider-bold">
        <table class="receipt-totals">
            ${totalRow}
        </table>
        ${footer}
    `;
}

/**
 * Print a receipt from a shadow-DOM widget.
 *
 * Widgets run inside shadow DOM on customer websites, so `window.print()` prints the
 * host page and Emotion styles scoped to the shadow root never reach the printer
 * (blank PDF on mobile, SB-908). We work around this by creating a hidden iframe on
 * `document.body` — outside all shadow roots — writing content via `srcdoc`, and
 * calling `contentWindow.print()` to open the browser's native print dialog.
 *
 * The iframe is intentionally left in the DOM after printing. Removing it caused
 * the print preview to go blank when the user adjusted zoom or printer settings.
 * A 1×1px invisible iframe is harmless; if the user prints again we create a fresh one.
 *
 * Cross-browser timing: load → body-check → requestAnimationFrame → print().
 * - Android Chrome fires `load` twice for srcdoc (once for about:blank, once for
 *   real content). The body-children check skips the empty one.
 * - iOS Safari fires `load` before content is painted. `requestAnimationFrame`
 *   gives it one paint cycle so the print preview isn't blank.
 * - The `printed` flag prevents the double-dialog that rAF alone caused on iOS.
 */
export function printWidgetReceipt(data: WidgetReceiptData): void {
    const html = buildReceiptHtml(data);
    const css = getWidgetReceiptCss();
    const srcdoc = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title></title>
<style>${css}</style>
</head>
<body>${html}</body>
</html>`;

    const iframe = document.createElement('iframe');
    // Use A4 dimensions so the browser has a real viewport for print layout.
    // 1×1px caused iOS Safari to blank the preview when it re-rendered for a
    // discovered printer. Off-screen positioning keeps it invisible.
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.top = '0';
    iframe.style.width = '210mm';
    iframe.style.height = '297mm';
    iframe.style.border = 'none';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.srcdoc = srcdoc;
    document.body.appendChild(iframe);

    let printed = false;

    iframe.addEventListener('load', () => {
        if (printed) return;

        // Skip the about:blank load on Android Chrome — wait for real content.
        const body = iframe.contentDocument?.body;
        if (!body || body.children.length === 0) return;

        printed = true;

        // Give the browser one paint cycle so iOS Safari has content visible
        // before the print dialog opens (load fires before paint on iOS).
        requestAnimationFrame(() => {
            try {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
            } catch {
                // Some browsers block cross-origin print on iframes
            }
        });
    });
}
